import React from "react";
import { Environment, graphql, useMutation } from "react-relay";
import { CompetitionRulesPageUpdateCompetitionMutation as CompetitionRulesPageUpdateCompetitionMutationType } from "./__generated__/CompetitionRulesPageUpdateCompetitionMutation.graphql";
import { useIntl, FormattedMessage, defineMessage } from "react-intl";
import EditMarkdownSection from "@/components/EditMarkdownSection";
import CompetitionAcceptRulesButton from "@/components/CompetitionAcceptRulesButton";
import Loading from "@/components/Loading";
import { logger } from "@/common/logger";
import {
  CompetitionRulesPageQuery$data,
  CompetitionRulesPageQuery as CompetitionRulesPageQueryType,
} from "./__generated__/CompetitionRulesPageQuery.graphql";
import { LoaderArgs } from "@/utils/route";
import { preload, usePreloaded } from "@/utils/relay";
import { ActivityNotFound } from "@/components/ActivityNotFound";

const CompetitionRulesPageUpdateCompetitionMutation = graphql`
  mutation CompetitionRulesPageUpdateCompetitionMutation(
    $id: ID!
    $input: UpdateCompetitionInput!
  ) {
    updateCompetition(id: $id, input: $input) {
      node {
        id
        latestRule {
          id
          text
          entityAgreement {
            id
            createdAt
          }
        }
      }
    }
  }
`;
export default function CompetitionRulesPage() {
  const { query } = useCompetitionRulesPageQuery();
  if (!query.competitionBySlug) {
    return <ActivityNotFound kind="competition" />;
  }
  const competition = query.competitionBySlug;
  return (
    <CompetitionRulesEditMarkdownSection
      competition={competition}
      viewer={query.viewer}
    />
  );
}
interface CompetitionRulesEditMarkdownSectionProps {
  competition: NonNullable<CompetitionRulesPageQuery$data["competitionBySlug"]>;
  viewer: CompetitionRulesPageQuery$data["viewer"];
}
function CompetitionRulesEditMarkdownSection({
  competition,
  viewer,
}: CompetitionRulesEditMarkdownSectionProps) {
  const intl = useIntl();
  const [commitMutation, isMutationInFlight] =
    useMutation<CompetitionRulesPageUpdateCompetitionMutationType>(
      CompetitionRulesPageUpdateCompetitionMutation,
    );
  return (
    <EditMarkdownSection
      id={competition.id}
      canEdit={competition.viewerCanUpdate}
      disabled={isMutationInFlight}
      content={competition.latestRule.text}
      title={
        <div className="flex flex-row space-x-5">
          <div className="grow">
            <h2 className="text-2xl font-bold w-full font-poppins">
              <FormattedMessage defaultMessage="Rules" />
            </h2>
            <p className="py-2">
              <FormattedMessage defaultMessage="By default all tracks must comply to the rules defined in" />{" "}
              <a
                href="https://aqora.io/terms"
                target="_blank"
                rel="noreferrer"
                className="text-blue-400"
              >
                <FormattedMessage defaultMessage="aqora's Terms of Use" />
              </a>
            </p>
          </div>
          <div className="self-center">
            {viewer && competition.viewerCanAccept && (
              <React.Suspense fallback={<Loading className="w-10" />}>
                <CompetitionAcceptRulesButton competition={competition} />
              </React.Suspense>
            )}
          </div>
        </div>
      }
      onSubmit={({ content }) =>
        new Promise<void>((resolve, reject) =>
          commitMutation({
            variables: {
              id: competition.id,
              input: { rules: content || null },
            },
            onCompleted: () => resolve(),
            onError: (error) => {
              logger.error(error);
              reject(
                intl.formatMessage({
                  defaultMessage:
                    "An error occurred while saving the rules. Please, try again later.",
                }),
              );
            },
          }),
        )
      }
      canUploadFiles
    />
  );
}
const CompetitionRulesPageQuery = graphql`
  query CompetitionRulesPageQuery($slug: String!) {
    viewer @ifAllowed {
      id
    }
    competitionBySlug(slug: $slug) {
      id
      title
      latestRule {
        id
        text
      }
      viewerCanUpdate: viewerCan(action: UPDATE_COMPETITION)
      viewerCanAccept: viewerCan(action: CREATE_COMPETITION_RULE_AGREEMENT)
      ...CompetitionAcceptRulesButtonCompetitionFragment
    }
  }
`;
export const loader = (environment: Environment) => {
  return ({ params: { track: slug } }: LoaderArgs) => {
    if (!slug) {
      throw new Response("No slug", { status: 500 });
    }
    return preload<CompetitionRulesPageQueryType>(
      environment,
      CompetitionRulesPageQuery,
      { slug },
    );
  };
};
const useCompetitionRulesPageQuery = () =>
  usePreloaded<CompetitionRulesPageQueryType>();

export const lazyHandle = {
  titleI18n: defineMessage({ defaultMessage: "Rules" }),
};
